/**
 * Spalten und Zuschnitt der sechs Asset-Routen — reine Daten.
 *
 * Steht getrennt von `Assets.tsx`, damit `App.tsx` die Routenliste lesen kann,
 * **ohne** die Ansicht mitzuladen. Vorher hing `KONFIG` in `Assets.tsx`, und
 * eine einzige Zeile auf Modulebene
 *
 *     const ROUTEN = new Set([..., ...Object.keys(ASSET_KONFIG)]);
 *
 * zog damit die gesamte Assetansicht ins Startbuendel — samt Tabelle,
 * Baumansicht und allem, was sie ihrerseits braucht. Eine Konfiguration ist
 * kein Grund, eine Ansicht zu laden, die niemand geoeffnet hat.
 */

import type { Modul } from '../entwurf/navigation';

/** Eine Spalte: Feld, Beschriftung, Breite, Darstellung. */
export interface Spalte {
  feld: string;
  label: string;
  breite: number;
  art?: 'mono' | 'zahl' | 'geld' | 'marker' | 'idp' | 'jaNein' | 'art' | 'verweis'
    | 'rollen' | undefined;
  /** Entfällt in Mandanten ohne Personenbezug. */
  personenbezogen?: boolean | undefined;
  /**
   * Entfällt, wenn das Modul in diesem Mandanten abgeschaltet ist.
   *
   * Ausgeblendet wird die **Spalte**, nicht das Feld: Datenbank, Import und
   * Schreibpfad bleiben unverändert, und ein Mandant, der die Einstufung
   * führt, sieht sie weiterhin.
   */
  modul?: Modul | undefined;
}

export interface Konfig {
  titel: string;
  sammlung: 'assets' | 'gruppen';
  /** Feste Einschränkung auf eine Asset-Art. */
  typ?: string;
  /** Zwei Bereiche in einer Route, z. B. Postfächer und Verteiler. */
  reiter?: [schluessel: string, label: string, typ: string][];
  /** Verzeichnisbaum links (C2). */
  baum?: boolean;
  suche: string;
  neu: string;
  leerTitel: string;
  spalten: Spalte[];
  /** Schalter, die wirklich einschränken: [Schlüssel, Label, Titel, Feld, Wert]. */
  schalter: [schluessel: string, label: string, titel: string, feld: string, wert: string,
    modul?: Modul][];
}

const S = (feld: string, label: string, breite: number, art?: Spalte['art'],
  personenbezogen?: boolean, modul?: Modul): Spalte =>
  ({ feld, label, breite, art, personenbezogen, modul });

/**
 * Felder, für die es eine Werteliste im Spaltenkopf gibt.
 *
 * Nur was in `apps/api/src/sammlungen.ts` als `filterbar` gemeldet ist — und
 * davon nur, was wenige Ausprägungen hat. Schlüssel und Namen bleiben aussen
 * vor: eine Liste mit einem Eintrag je Zeile filtert niemand, danach sucht man.
 */
/* Die Spalte „Status" ist am 27.08.2026 aus allen fuenf Assetlisten
   entfallen.

   Der Kunde hat es gemeldet: „beim Exchange-Postfach ist ein Status als
   Spalte, aber ich kann gar keinen Status angeben. Das Gleiche bei einem
   Laufwerk … genau das Gleiche auch bei den Applikationen."

   Beides stimmt, und der Grund ist derselbe wie bei der Kritikalitaet:
   `werkzeuge/matrix-lesen.py` legt `status` an Assets **leer** an — die
   Standardrollenmatrix fuehrt das Feld nicht. Beim Kunden stand in der
   Spalte also ueberall ein Strich, und kein Formular konnte etwas
   hineinschreiben. Eine Spalte, die nichts zeigt und die niemand fuellen
   kann, sieht aus wie eine Auskunft und ist keine.

   **Die Spalte bleibt im Schema** (`iam.Asset.Status`), der Import und der
   Schreibpfad sind unveraendert. Ausgeblendet ist die Anzeige; das laesst
   sich zuruecknehmen, ein geloeschtes Feld nicht.

   Nicht angefasst: „Status" an den Rollen und an den Klaerungspunkten. Dort
   traegt das Feld etwas — bei Rollen den Wert aus `iam.Aufzaehlung`
   (`aktiv`, `archiviert`, …), und der Umschalter im Katalog haengt daran.

   Gemessen und benannt, aber nicht geaendert: **`idp` ist an Assets ebenso
   leer** (`matrix-lesen.py:371`). Die Spalte steht an Laufwerken, Exchange
   und M365 weiter — sie zu entfernen war nicht verlangt, und anders als beim
   Status laesst sie sich im Formular setzen. */
export const KONFIG: Record<string, Konfig> = {
  '/assets/applikationen': {
    /* Mit den mobilen Apps (HOWOGE, Punkt 18, 07.10.2026). Der Import liefert
       sie als `MobileApp`; umgeschrieben werden sie bewusst nicht, sonst
       fielen gleichnamige Desktop- und Mobil-Apps in Zaehlern und Lizenz-
       kosten zusammen. Das Komma ist in den Filtern der Schnittstelle ein
       ODER. */
    titel: 'Applikationen', sammlung: 'assets', typ: 'Applikation,MobileApp',
    suche: 'Name, Hersteller oder Kategorie…', neu: '+ Neu',
    leerTitel: 'Keine Anwendung erfasst',
    spalten: [
      S('name', 'Name', 300), S('kategorie', 'Kategorie', 200),
      S('hersteller', 'Hersteller', 180),
      /* Die Spalte „Version" ist am 28.08.2026 entfallen — auf Wunsch des
         Kunden: die Softwareversion wird vorerst nicht gepflegt. Das Feld
         verschwindet ebenso aus dem Formular; `iam.Asset.Version` bleibt in
         der Datenbank, damit nichts verloren geht. */
      /* Zwei Verantwortungen, seit dem 26.08.2026 getrennt — der Kunde hat
         es als Show-Stopper gemeldet. `fachlicherOwner` war bis dahin ueber
         kein Formular ausser „Team" erreichbar und stand in keiner Liste. */
      S('fachlicherOwner', 'Fachlicher Owner', 180, undefined, true),
      S('ownerName', 'Technischer Owner', 180, undefined, true),
      S('berechtigungskonzeptVorhanden', 'Berechtigungskonzept', 220),
      S('anzahlRollen', 'Rollen', 110, 'rollen'),
      S('anzahlNutzer', 'Nutzer', 110, 'zahl', true),
      S('lizenzkostenProUserJahr', 'Kosten/Nutzer', 150, 'geld'),
      S('kritikalitaet', 'Kritikalität', 140, 'marker', false, 'kritikalitaet'),
    ],
    schalter: [],
  },
  '/assets/fileserver': {
    titel: 'Fileserver & Laufwerke', sammlung: 'assets', typ: 'Fileserver', baum: true,
    suche: 'Pfad oder Bezeichnung…', neu: '+ Neu',
    leerTitel: 'Keine Laufwerksberechtigung',
    spalten: [
      S('name', 'Pfad', 380, 'mono'),
      /* „Bezeichnung" (`kategorie`) und „IDP" sind am 28.08.2026 entfallen.
         Beide sagen an jedem Laufwerk dasselbe: die Bezeichnung steht im
         ganzen Bestand auf „Laufwerk-Zugriffe", `idp` ist durchgehend leer
         (`matrix-lesen.py` legt es ausdruecklich leer an). Eine Spalte, in
         der ueberall derselbe Wert steht, sieht aus wie eine Auskunft und
         ist keine.

         Das **Feld** bleibt: `kategorie` traegt die Zuordnung und wird beim
         Anlegen weiterhin gesetzt. */
      S('anzahlRollen', 'Rollen', 110, 'rollen'),
      S('anzahlNutzer', 'Personen', 120, 'zahl', true),
      S('kritikalitaet', 'Kritikalität', 140, 'marker', false, 'kritikalitaet'),
    ],
    schalter: [],
  },
  '/assets/exchange': {
    titel: 'Exchange', sammlung: 'assets',
    reiter: [['postfaecher', 'Funktionspostfächer', 'Postfach'],
      ['verteiler', 'E-Mail-Verteiler', 'Verteiler']],
    suche: 'Adresse oder Anzeigename…', neu: '+ Neu',
    leerTitel: 'Kein Eintrag erfasst',
    spalten: [
      S('name', 'Adresse', 320, 'mono'), S('kategorie', 'Bezeichnung', 220),
      S('ownerName', 'Owner', 180, undefined, true),
      S('anzahlRollen', 'Rollen', 110, 'rollen'),
      S('anzahlNutzer', 'Personen', 120, 'zahl', true),
      /* „IDP" entfallen am 28.08.2026 — im Bestand durchgehend leer, wie an
         den Laufwerken. */
    ],
    schalter: [],
  },
  '/assets/m365': {
    titel: 'M365 & Teams', sammlung: 'assets', typ: 'M365',
    suche: 'Team oder Gruppenname…', neu: '+ Neu',
    leerTitel: 'Kein Team erfasst',
    spalten: [
      S('name', 'Name', 300), S('kategorie', 'Typ', 220),
      S('fachlicherOwner', 'Fachlicher Owner', 180, undefined, true),
      S('ownerName', 'Technischer Owner', 180, undefined, true),
      S('anzahlNutzer', 'Mitglieder', 130, 'zahl', true),
      S('anzahlRollen', 'Rollen', 110, 'rollen'),
      S('kritikalitaet', 'Vertraulichkeit', 160, 'marker', false, 'kritikalitaet'),
    ],
    schalter: [],
  },
  '/assets/hardware': {
    /* Bis zum 28.08.2026 „Hardware & Mobile" mit zwei Reitern. Die mobilen
       Anwendungen sind Anwendungen und stehen jetzt bei den Applikationen,
       unter der Kategorie „Apps für mobile Endgeräte" (`052`). Damit bleibt
       hier genau eine Sache — und ein Reiterband über einem einzigen Reiter
       ist ein Bedienelement ohne Wahl. */
    titel: 'Hardware', sammlung: 'assets', typ: 'Hardware',
    suche: 'Bezeichnung oder Geräteklasse…', neu: '+ Neu',
    leerTitel: 'Kein Eintrag erfasst',
    spalten: [
      S('name', 'Bezeichnung', 320), S('kategorie', 'Geräteklasse', 220),
      /* Der Warenkorb des Kunden. Bis zum 25.08.2026 stand er als „(Link zum
         Warenkorb)" **im Namen** des Geraets — als Text, der auf einen Link
         hinweist und keiner ist. Er steht jetzt in `iam.Asset.Verweis`. */
      S('verweis', 'Warenkorb', 190, 'verweis'),
      /* Die Zahl fuehrt in die Matrix, gefiltert auf dieses Paket. Sie stand
         dort seit jeher — nur kam man von ihr aus nirgendwohin, und die Frage
         „welche Rollen bekommen das" ist genau die, die man an einem
         Ausstattungspaket stellt.

         Seit dem 27.08.2026 traegt **jede** Rollenzahl diese Blase, nicht nur
         die hier. Sie stand ab dem 26.08. an den Ausstattungspaketen, und der
         Kunde hat am Tag darauf gefragt, warum ausgerechnet dort — die Frage
         „welche Rollen sind das" stellt sich an einer Anwendung, einem
         Laufwerk und einer technischen Gruppe genauso. Ueberall sonst war die
         Zahl bis dahin eine Sackgasse. */
      S('anzahlRollen', 'Rollen', 110, 'rollen'),
      S('anzahlNutzer', 'Zugeordnet', 140, 'zahl', true),
      S('lizenzkostenProUserJahr', 'Beschaffung', 150, 'geld'),
    ],
    schalter: [],
  },
  '/assets/gruppen': {
    titel: 'Technische Gruppen', sammlung: 'gruppen',
    suche: 'Gruppenname oder Group ID…', neu: '+ Neu',
    leerTitel: 'Noch keine technische Gruppe hinterlegt',
    spalten: [
      S('name', 'Gruppen-Name', 380, 'mono'), S('groupId', 'Group ID', 160, 'mono'),
      S('idp', 'IDP', 110, 'idp'), S('gruppentyp', 'Gruppentyp', 180),
      S('zielsystem', 'Zielsystem', 200),
      /* Mitglieder und Konvention liefert nur ein Abgleich mit dem
         Verzeichnis — `module.gruppenabgleich`. */
      S('mitgliederAnzahl', 'Mitglieder', 130, 'zahl', true, 'gruppenabgleich'),
      S('verwendetInRollen', 'In Rollen', 130, 'rollen'),
      S('konform', 'Konvention', 140, 'jaNein', false, 'gruppenabgleich'),
    ],
    schalter: [['abweichend', 'nur konventionswidrig',
      'Gruppen, die vom Namensschema abweichen', 'konform', 'false', 'gruppenabgleich']],
  },
};
